import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useGridApiRef } from '@mui/x-data-grid';
import DescriptionIcon from '@mui/icons-material/Description';
import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';

function matchesSearchText(study, searchText) {
  if (!searchText) return true;
  const searchLower = searchText.toLowerCase();
  return (
    (study.study_description || '').toLowerCase().includes(searchLower)
    || (study.modality || '').toLowerCase().includes(searchLower)
    || (study.study_date || '').includes(searchLower)
  );
}

export default function usePriorStudies(patientId, { onShowPriorReportFromIcon } = {}) {
  const { t, i18n } = useTranslation();
  const [priorStudies, setPriorStudies] = useState([]);
  const [priorStudiesSearchText, setPriorStudiesSearchText] = useState('');
  const [priorStudiesModalityFilter, setPriorStudiesModalityFilter] = useState('all');
  const [showPriorReportText, setShowPriorReportText] = useState(false);
  const [selectedPriorStudy, setSelectedPriorStudy] = useState(null);
  const priorStudiesApiRef = useGridApiRef();
  const lastOpenedPriorStudyIdRef = useRef(null);

  const loadPriorStudies = useCallback(async (pid) => {
    if (!pid) {
      setPriorStudies([]);
      return;
    }
    try {
      const response = await fetch(createApiUrl(`prior-studies/?patient_id=${pid}`), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPriorStudies(data);
      }
    } catch (err) {
      console.error('Error loading prior studies:', err);
    }
  }, []);

  useEffect(() => {
    loadPriorStudies(patientId);
  }, [patientId, loadPriorStudies]);

  const clearPriorStudies = useCallback(() => {
    setPriorStudies([]);
    setPriorStudiesSearchText('');
    setPriorStudiesModalityFilter('all');
    setShowPriorReportText(false);
    setSelectedPriorStudy(null);
    lastOpenedPriorStudyIdRef.current = null;
  }, []);

  const getUniqueModalities = useCallback(() => {
    const modalities = [...new Set(priorStudies.map((study) => study.modality).filter(Boolean))];
    return modalities.sort();
  }, [priorStudies]);

  const getFilteredAndSortedPriorStudies = useCallback(() => {
    const filtered = [...priorStudies].filter((study) => {
      const matchesModality = priorStudiesModalityFilter === 'all' || study.modality === priorStudiesModalityFilter;
      return matchesModality && matchesSearchText(study, priorStudiesSearchText);
    });

    filtered.sort((a, b) => {
      const dateA = new Date(a.study_date || '1900-01-01');
      const dateB = new Date(b.study_date || '1900-01-01');
      return dateB.getTime() - dateA.getTime();
    });

    return filtered;
  }, [priorStudies, priorStudiesModalityFilter, priorStudiesSearchText]);

  const handleShowPriorReportFromIcon = useCallback((study, event) => {
    event?.stopPropagation?.();
    lastOpenedPriorStudyIdRef.current = study.id || study.study_instance_uid;
    setSelectedPriorStudy(study);
    setShowPriorReportText(true);
    onShowPriorReportFromIcon?.(study, event);
  }, [onShowPriorReportFromIcon]);

  const handleBackFromPriorReportText = useCallback(() => {
    setShowPriorReportText(false);
    setSelectedPriorStudy(null);
  }, []);

  useEffect(() => {
    if (!showPriorReportText && lastOpenedPriorStudyIdRef.current && priorStudiesApiRef.current) {
      const timer = setTimeout(() => {
        try {
          const rowId = lastOpenedPriorStudyIdRef.current;
          const allRows = priorStudiesApiRef.current.getAllRowIds();
          const rowIndex = allRows.indexOf(rowId);
          if (rowIndex !== -1) {
            priorStudiesApiRef.current.scrollToIndexes({ rowIndex });
          }
          lastOpenedPriorStudyIdRef.current = null;
        } catch (error) {
          console.error('Fehler beim Scrollen zur Row:', error);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showPriorReportText, priorStudiesApiRef]);

  const priorStudiesColumns = useMemo(() => [
    {
      field: 'report',
      headerName: '',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <DescriptionIcon sx={{ color: 'text.secondary' }} />
      ),
      renderCell: (params) => {
        const hasReport = params.row.has_report;
        if (!hasReport) return null;
        return (
          <IconButton
            size="small"
            onClick={(event) => handleShowPriorReportFromIcon(params.row, event)}
            sx={{
              color: 'var(--rd-primary)',
              padding: '4px',
              '&:hover': { backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)' },
            }}
          >
            <DescriptionIcon />
          </IconButton>
        );
      },
    },
    {
      field: 'study_date',
      headerName: t('report.date', 'Datum'),
      width: 110,
      sortable: false,
      valueFormatter: (value) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          return !isNaN(date.getTime())
            ? date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '-';
        } catch {
          return '-';
        }
      },
    },
    {
      field: 'modality',
      headerName: t('report.modality', 'Modalität'),
      width: 100,
      sortable: false,
    },
    {
      field: 'study_description',
      headerName: t('report.studyDescription', 'Studienbeschreibung'),
      width: 380,
      sortable: false,
    },
    {
      field: 'referring_physician',
      headerName: t('report.referringPhysician', 'Zuweisender Arzt'),
      width: 230,
      sortable: false,
    },
  ], [t, handleShowPriorReportFromIcon]);

  const getGridLocale = useCallback(() => {
    if (i18n.language === 'de') {
      return {
        noRowsLabel: t('report.table.noRows', 'Keine Daten'),
        noResultsOverlayLabel: t('report.table.noResults', 'Keine Ergebnisse'),
        errorOverlayDefaultLabel: t('report.table.error', 'Fehler'),
        columnMenuSortAsc: t('report.table.sortAsc', 'Aufsteigend sortieren'),
        columnMenuSortDesc: t('report.table.sortDesc', 'Absteigend sortieren'),
        columnMenuFilter: t('report.table.filter', 'Filter'),
        columnMenuHideColumn: t('report.table.hideColumn', 'Spalte ausblenden'),
        columnMenuShowColumns: t('report.table.showColumns', 'Spalten anzeigen'),
        columnMenuManageColumns: t('report.table.manageColumns', 'Spalten verwalten'),
        columnMenuUnsort: t('report.table.unsort', 'Sortierung aufheben'),
        columnMenuFilterLabel: t('report.table.filterLabel', 'Filter'),
        columnMenuFilterPlaceholder: t('report.table.filterPlaceholder', 'Wert eingeben...'),
        columnMenuFilterOk: t('report.table.filterOk', 'OK'),
        columnMenuFilterReset: t('report.table.filterReset', 'Zurücksetzen'),
        toolbarColumns: t('report.table.toolbarColumns', 'Spalten'),
        toolbarColumnsLabel: t('report.table.toolbarColumnsLabel', 'Spalten auswählen'),
        columnsPanelTextFieldLabel: t('report.table.columnsPanelTextFieldLabel', 'Spalte suchen'),
        columnsPanelShowAllButton: t('report.table.columnsPanelShowAllButton', 'Alle anzeigen'),
        columnsPanelHideAllButton: t('report.table.columnsPanelHideAllButton', 'Alle ausblenden'),
      };
    }
    return {};
  }, [i18n.language, t]);

  return {
    priorStudies,
    priorStudiesSearchText,
    setPriorStudiesSearchText,
    priorStudiesModalityFilter,
    setPriorStudiesModalityFilter,
    showPriorReportText,
    selectedPriorStudy,
    priorStudiesApiRef,
    getUniqueModalities,
    getFilteredAndSortedPriorStudies,
    priorStudiesColumns,
    getGridLocale,
    handleBackFromPriorReportText,
    handleShowPriorReportFromIcon,
    loadPriorStudies,
    clearPriorStudies,
  };
}
